import { LitElement, html, css, nothing } from 'lit';
import { StoreMixin } from '../store.js';
import { api } from '../api.js';
import { navigate } from '../router.js';
import { loadEstimate } from '../estimate.js';
import { planAmounts, planTotals } from '../lib/schedule.js';
import { count, hours as fmtHours, mediumDate, money } from '../lib/format.js';
import { sizeTierLabel } from '../lib/size.js';

/** Long enough that typing into an hours box does not fire a request per keystroke. */
const SAVE_DEBOUNCE_MS = 400;

/** How often the screen re-reads a plan while a write is running: one task lands every ~2.4 s. */
const WRITE_POLL_MS = 2000;

/**
 * Create Schedule (Estimate Tool 8.1, 8.7; Phase E10) — `/estimate/:id/schedule`.
 *
 * The estimator picks a client and one of that client's **existing** Workamajig projects, gives a
 * delivery date, and gets a complete schedule — groups, tasks, and assignments by service — to
 * argue with before anything is written. Nothing on this screen reaches Workamajig except one
 * read: the task count of the project that was chosen.
 *
 * Five things the design is particular about, and they are why this file is the size it is.
 *
 * **The size is not asked for here.** It moved to the estimate editor on 2026-09-20, when the
 * size analog became a pricing input (6.8): it decides which past projects are comparable, so it
 * has to be chosen before there is a price to look at. A second copy on this screen would let an
 * estimate and its schedule disagree about how big the job is, silently. This screen shows what
 * the estimate said and links back to it. The delivery date defaults from the estimate for the
 * same reason and is still editable here, because a commitment can move after a quote goes out.
 *
 * **It has to be a real project.** Workamajig tasks hang off a project number, and until the work
 * is sold there is nothing to hang them on (8.1). The estimate's own project name is a name on a
 * document. So the picker lists the account's projects for that client and never offers to create
 * one — the E8 spike settled that by finding a per-task write into a project that already exists.
 *
 * **A project that already carries tasks needs an explicit confirm.** Generating onto one is how a
 * schedule gets written twice, and there is no delete through the API (8.8): a duplicate is
 * cleared up by hand, task by task, in Workamajig's own UI. The count is on every row of the
 * picker, and the confirm is a separate deliberate act.
 *
 * **The plan is a proposal, not an answer.** Every line is editable — hours, service, rename,
 * removal — and an edited line is marked, so a calibrated line and a hand-edited one stay
 * distinguishable at the review gate. Removing a group removes its children.
 *
 * **The draft persists on every change.** A patch goes to the server as each edit is made, and the
 * totals redraw from the same pure function the server stores (`planTotals`), so the group
 * subtotal on screen and the stored total cannot disagree about what a removal meant.
 *
 * **No control here names a person.** A generated assignment is a service and its hours and
 * nothing else (8.3). There is no field for a person, because staffing is the Forecast tool's
 * subject and traffic's decision — and because leaving the assignment at the service is what lets
 * the Forecast tool read this schedule as demand by discipline the moment it is written.
 */
class EstimateSchedule extends StoreMixin(LitElement) {
  static properties = {
    route: { type: Object },
    _estimate: { state: true },
    _plan: { state: true },
    _projects: { state: true },
    _services: { state: true },
    _ladder: { state: true },
    _form: { state: true },
    _generating: { state: true },
    _saving: { state: true },
    _error: { state: true },
    _loadError: { state: true },
    _collapsed: { state: true },
    _showRemoved: { state: true },
    _approving: { state: true },
    _approveError: { state: true },
    _writing: { state: true },
    _writeError: { state: true },
  };

  constructor() {
    super();
    this.route = null;
    this._estimate = null;
    this._plan = null;
    this._projects = null;
    this._services = [];
    this._ladder = [];
    this._form = null;
    this._generating = false;
    this._saving = false;
    this._error = null;
    this._loadError = null;
    this._collapsed = new Set();
    this._showRemoved = false;
    this._approving = false;
    this._approveError = null;
    this._writing = false;
    this._writeError = null;
    this._queue = { tasks: new Map(), assignments: new Map() };
    this._timer = null;
    this._pollTimer = null;
  }

  static styles = css`
    :host {
      display: block;
    }
    .card {
      background: var(--surface);
      border: 1px solid var(--border-soft);
      border-radius: var(--radius-card);
      box-shadow: var(--shadow-card);
      padding: 20px 24px 18px;
      margin-bottom: 18px;
    }
    .eyebrow {
      font-family: var(--font-eyebrow);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: var(--ink-muted);
    }
    h2 {
      margin: 6px 0 4px;
      font-size: 20px;
      color: var(--ink-strong);
    }
    h3 {
      margin: 0 0 10px;
      font-size: 14px;
      color: var(--ink-strong);
    }
    .sub {
      margin: 0 0 16px;
      font-size: 13px;
      color: var(--ink-muted);
    }
    .setup {
      display: grid;
      gap: 12px 18px;
      grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
      align-items: start;
    }
    .field {
      display: grid;
      gap: 5px;
    }
    label {
      font-size: 13px;
      font-weight: 700;
      color: var(--ink-strong);
    }
    input,
    select {
      font: inherit;
      font-size: 14px;
      padding: 7px 9px;
      border: 1px solid var(--control-border);
      border-radius: 6px;
      background: var(--control-bg);
      color: var(--ink-strong);
      width: 100%;
      box-sizing: border-box;
    }
    .hint {
      font-size: 12px;
      color: var(--ink-muted);
      line-height: 1.45;
    }
    /* A value this screen shows rather than asks for: the size, which lives on the estimate. */
    .label-as-text {
      font-size: 13px;
      font-weight: 700;
      color: var(--ink-strong);
    }
    .inherited {
      display: grid;
      gap: 4px;
      font-size: 14px;
      color: var(--ink-strong);
      padding-top: 6px;
    }
    button.link {
      border: 0;
      background: none;
      padding: 0;
      font: inherit;
      font-size: 12px;
      color: var(--ink-muted);
      text-decoration: underline;
      cursor: pointer;
    }
    .actions {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
      margin-top: 16px;
      padding-top: 14px;
      border-top: 1px solid var(--border-soft);
    }
    button {
      font: inherit;
      font-size: 13px;
      font-weight: 600;
      padding: 8px 16px;
      border: 1px solid var(--control-border);
      border-radius: 6px;
      background: var(--surface);
      color: var(--ink-strong);
      cursor: pointer;
    }
    button.primary {
      background: var(--ink-strong);
      color: var(--surface);
      border-color: var(--ink-strong);
    }
    button[disabled] {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .error {
      color: var(--status-overloaded);
      font-weight: 600;
      font-size: 13px;
    }
    .state {
      color: var(--ink-muted);
      padding: 16px 0;
    }
    /* The confirm a project with a schedule needs: a warning the reader has to answer, not a
       tooltip they can miss. */
    .warn {
      margin: 12px 0 0;
      padding: 10px 12px;
      border: 1px solid var(--border);
      border-left: 3px solid var(--status-caution, var(--ink-muted));
      border-radius: 6px;
      background: var(--surface-2);
      font-size: 13px;
      line-height: 1.5;
    }
    .warn label {
      display: flex;
      gap: 8px;
      align-items: flex-start;
      font-weight: 600;
      margin-top: 8px;
    }
    .warn input[type='checkbox'] {
      width: auto;
      margin-top: 2px;
    }
    /* The approval gate (8.7): the last thing between a draft and a write with no undo. */
    .approval {
      font-size: 13px;
      line-height: 1.55;
      color: var(--ink);
    }
    .approval strong {
      color: var(--ink-strong);
    }
    .approval.approved {
      border-left: 3px solid var(--status-healthy, var(--ink-muted));
      padding-left: 12px;
    }
    .progress {
      height: 6px;
      border-radius: 3px;
      background: var(--border-soft);
      overflow: hidden;
      margin: 10px 0 6px;
    }
    .progress > div {
      height: 100%;
      background: var(--ink-strong);
      transition: width 0.4s;
    }
    .findings {
      margin: 8px 0 0;
      padding-left: 18px;
      font-size: 12px;
    }
    .findings li {
      margin: 2px 0;
    }
    .mark.written {
      color: var(--status-healthy-ink, inherit);
    }
    .mark.failed {
      color: var(--status-overloaded, #b3261e);
    }
    .approval.done {
      border-left: 3px solid var(--ink-muted);
      padding-left: 12px;
      color: var(--ink-muted);
    }
    .approval .hint {
      margin: 8px 0 0;
    }
    .approval-actions {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
      margin-top: 12px;
    }
    .approval-actions .hint {
      margin: 0;
      max-width: 62ch;
    }
    .stale {
      color: var(--status-caution, var(--ink-strong));
    }
    .locked {
      font-family: var(--font-eyebrow);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--ink-muted);
    }
    /* The totals strip: the numbers the gate reconciles, side by side. */
    .totals {
      display: grid;
      gap: 14px 22px;
      grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
      margin-bottom: 4px;
    }
    .stat .k {
      font-family: var(--font-eyebrow);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--ink-muted);
      display: block;
    }
    .stat .v {
      font-size: 19px;
      font-weight: 700;
      font-variant-numeric: tabular-nums;
      color: var(--ink-strong);
    }
    .stat .n {
      font-size: 12px;
      color: var(--ink-muted);
    }
    .off {
      color: var(--status-caution, var(--ink-strong));
    }
    .services {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid var(--border-soft);
    }
    .chip {
      font-size: 12px;
      padding: 3px 8px;
      border: 1px solid var(--border-soft);
      border-radius: 999px;
      color: var(--ink);
      font-variant-numeric: tabular-nums;
      white-space: nowrap;
    }
    .scroll {
      overflow-x: auto;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 13px;
    }
    th {
      text-align: left;
      font-family: var(--font-eyebrow);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--ink-muted);
      padding: 6px 8px;
      border-bottom: 1px solid var(--border);
      white-space: nowrap;
    }
    td {
      padding: 4px 8px;
      border-bottom: 1px solid var(--border-soft);
      vertical-align: middle;
    }
    td.num,
    th.num {
      text-align: right;
      font-variant-numeric: tabular-nums;
    }
    .after {
      font-size: 11px;
      color: var(--ink-muted);
      padding: 0 6px 2px;
    }
    .after .gone {
      text-decoration: line-through;
    }
    tfoot td {
      border-top: 2px solid var(--border);
      border-bottom: 0;
      font-weight: 700;
      color: var(--ink-strong);
    }
    tfoot .hint {
      font-weight: 400;
    }
    tr.group > td {
      background: var(--surface-2);
      font-weight: 700;
      color: var(--ink-strong);
    }
    tr.removed > td {
      opacity: 0.45;
    }
    tr.removed .name {
      text-decoration: line-through;
    }
    .name {
      font: inherit;
      border: 1px solid transparent;
      background: none;
      color: inherit;
      padding: 4px 6px;
      border-radius: 5px;
      width: 100%;
      box-sizing: border-box;
      font-weight: inherit;
    }
    .name:hover,
    .name:focus {
      border-color: var(--control-border);
      background: var(--control-bg);
    }
    .indent-1 {
      padding-left: 18px;
    }
    .indent-2 {
      padding-left: 36px;
    }
    .lines {
      display: grid;
      gap: 4px;
    }
    .line {
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .line select {
      width: auto;
      min-width: 13ch;
      padding: 3px 6px;
      font-size: 12px;
    }
    .line input[type='number'] {
      width: 8ch;
      text-align: right;
      padding: 3px 6px;
      font-size: 12px;
      font-variant-numeric: tabular-nums;
    }
    .icon {
      padding: 2px 7px;
      font-size: 12px;
      line-height: 1.4;
    }
    /* A hand-edited line and a calibrated one have to stay distinguishable (E10). */
    .mark {
      font-family: var(--font-eyebrow);
      font-size: 9px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      padding: 1px 5px;
      border-radius: 4px;
      border: 1px solid var(--border-soft);
      color: var(--ink-muted);
      white-space: nowrap;
    }
    .mark.edited {
      color: var(--status-caution, var(--ink-strong));
      border-color: currentColor;
    }
    .was {
      font-size: 11px;
      color: var(--ink-muted);
      font-variant-numeric: tabular-nums;
    }
    .toolbar {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
      margin-bottom: 10px;
    }
    .toolbar label {
      display: flex;
      align-items: center;
      gap: 6px;
      font-weight: 600;
      font-size: 12px;
      color: var(--ink-muted);
    }
    .toolbar input[type='checkbox'] {
      width: auto;
    }
    .saving {
      font-size: 12px;
      color: var(--ink-muted);
      margin-left: auto;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    loadEstimate();
    this._boot();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this._timer);
    clearTimeout(this._pollTimer);
  }

  updated(changed) {
    if (changed.has('route') && this._bootedFor !== this._id) this._boot();
  }

  get _id() {
    return this.route?.params?.id ?? null;
  }

  get _companies() {
    return this.appState.estimate.data?.companies ?? [];
  }

  /** The rate sheets a budget can be converted through (8.11), from the bootstrap. */
  get _rateSheets() {
    return this.appState.estimate.data?.rate_sheets ?? [];
  }

  get _defaultSheet() {
    return this._rateSheets.find((r) => r.is_default) ?? null;
  }

  async _boot() {
    this._bootedFor = this._id;
    if (!this._id) return;
    try {
      const [{ estimate }, ladder, services] = await Promise.all([
        api.get(`/estimate/estimates/${this._id}`),
        api.get('/estimate/schedule/templates').catch(() => ({ templates: [] })),
        api.get('/estimate/schedule/services').catch(() => ({ services: [] })),
      ]);
      this._estimate = estimate;
      this._ladder = ladder.templates ?? [];
      this._services = services.services ?? [];
      this._form = {
        company_id: estimate.company_id ?? null,
        company_name: estimate.company_name ?? '',
        project_number: estimate.schedule?.target_project_number ?? '',
        confirm: false,
        // The size comes from the estimate now (6.8) — it is a pricing input, so it is chosen
        // where the price is. This screen shows it and does not ask for it, which is exactly
        // why it must not seed from the last plan's tier: that tier is history, and carrying it
        // forward would mean a size changed on the estimate could never take effect.
        tier: estimate.size_tier ?? '',
        target_hours: '',
        // The budget and its sheet come from the estimate too (6.10, 8.11), and stay editable
        // here: a number can move between the quote and the schedule.
        budget_amount: estimate.budget_amount ?? '',
        rate_sheet_id: estimate.rate_sheet_id ?? '',
        anchor: 'delivery',
        start_date: '',
        // As does the delivery date, where the estimate carries one (8.6): the commitment is
        // made when the work is quoted, and asking twice invites two answers.
        delivery_date: estimate.delivery_date ?? '',
      };
      // The plan the estimator left off with, if there is one (8.7): the draft is the work, and
      // coming back to this URL has to find it exactly as it was left.
      if (estimate.schedule?.id) {
        this._plan = await api.get(`/estimate/schedule/plans/${estimate.schedule.id}`);
        this._form = {
          ...this._form,
          // **Not the tier.** It comes from the estimate, above.
          //
          // And the target only when a person typed one. The plan records which it was, as
          // `target_hours_source`, so this does not have to guess: a target the *model*
          // derived is not an override, and re-sending it would pin the first generation's
          // answer for ever — a microsite cut against a target carried over from a medium plan
          // is a 5.7× scale, and the generator rightly refuses it.
          target_hours:
            this._plan.recommendation?.target_hours_source === 'estimator'
              ? String(this._plan.target_hours)
              : '',
          anchor: this._plan.schedule?.anchor ?? 'delivery',
          start_date: this._plan.schedule?.requested_start_date ?? '',
          delivery_date: this._plan.delivery_date ?? '',
          // The plan's own budget and sheet, so regenerating keeps what this plan was cut to.
          budget_amount: this._plan.budget_amount ?? '',
          rate_sheet_id: this._plan.rate_sheet_id ?? '',
          project_number: this._plan.target_project_number ?? '',
          confirm: Boolean(this._plan.target_project_confirmed_at),
        };
        if (this._plan.status === 'writing') this._watchWrite();
      }
      await this._loadProjects();
    } catch (err) {
      this._loadError = `Could not open this estimate: ${err.message}`;
    }
  }

  async _loadProjects() {
    const f = this._form;
    if (!f) return;
    const query = f.company_id
      ? `company_id=${encodeURIComponent(f.company_id)}`
      : f.company_name
        ? `company_name=${encodeURIComponent(f.company_name)}`
        : null;
    if (!query) {
      this._projects = { company: null, projects: [] };
      return;
    }
    try {
      this._projects = await api.get(`/estimate/schedule/projects?${query}`);
    } catch (err) {
      this._projects = { company: null, projects: [] };
      this._error = `Could not list that client’s projects: ${err.message}`;
    }
  }

  _set(key, value) {
    this._form = { ...this._form, [key]: value };
    this._error = null;
  }

  /** The project row the picker is currently on, or null. */
  get _project() {
    const number = this._form?.project_number;
    if (!number) return null;
    return (this._projects?.projects ?? []).find((p) => p.project_number === number) ?? null;
  }

  /**
   * A project that already carries a schedule may not be generated onto until somebody says so.
   * The server refuses it too — a disabled button is not a disabled endpoint — but the reason
   * belongs on screen, next to the count that caused it.
   */
  get _needsConfirm() {
    const p = this._project;
    return Boolean(p && p.task_count > 0);
  }

  get _canGenerate() {
    const f = this._form;
    if (!f || this._generating) return false;
    // Generating again replaces the draft, so an approved plan refuses it for the same reason
    // it refuses an edit: what was approved has to still be what is there (8.7). Sending it
    // back to draft is the way to regenerate — and setting a written plan aside is the way to
    // start over: an abandoned plan stays as the record and a new one is generated beside it.
    if (this._plan && !(this._plan.can_regenerate ?? this._editable)) return false;
    if (!f.delivery_date) return false;
    if (f.anchor === 'span' && !f.start_date) return false;
    if (!f.project_number) return false;
    if (this._needsConfirm && !f.confirm) return false;
    return true;
  }

  async _generate() {
    this._generating = true;
    this._error = null;
    const f = this._form;
    try {
      const made = await api.post('/estimate/schedule/plan', {
        estimate_id: this._id,
        // No tier. This screen has no control that asks a person for one (6.8), so it has no
        // business sending one — the server resolves it from the estimate's size analog, and
        // falls back to the analog recommendation when the estimate carries none.
        ...(Number(f.target_hours) > 0 ? { target_hours: Number(f.target_hours) } : {}),
        // The budget as the box now reads: a number scales the plan to it, an empty box means
        // no budget for this plan even when the estimate carries one (8.11).
        budget_amount: Number(f.budget_amount) > 0 ? Number(f.budget_amount) : null,
        rate_sheet_id: f.rate_sheet_id || null,
        ...(f.start_date ? { start_date: f.start_date } : {}),
        delivery_date: f.delivery_date,
        anchor: f.anchor,
        target_project_number: f.project_number,
        confirm_existing_tasks: Boolean(f.confirm),
        save: true,
      });
      // Read the stored plan back rather than drawing the generator's payload: the editor needs
      // the row ids an edit addresses, and they exist only once it is stored.
      this._plan = await api.get(`/estimate/schedule/plans/${made.id}`);
      // The tier the generator settled on, for display only — and the target box is left exactly
      // as the person left it. Writing the generator's own answer back into the request is how
      // the first generation's tier and target became permanent.
      this._form = { ...f, tier: this._plan.tier };
    } catch (err) {
      this._error = err.message;
    } finally {
      this._generating = false;
    }
  }

  // ------------------------------------------------------------------------------------------
  // Editing. Every change is applied locally at once and queued for the server; the totals are
  // recomputed from the same pure function the server stores, so the two cannot disagree.

  /** The plan's totals as the outline on screen now stands. */
  get _totals() {
    if (!this._plan) return null;
    return planTotals(
      this._plan.tasks.map((t) => ({
        ...t,
        removed_by_user: t.removed,
        assignments: t.assignments.map((a) => ({ ...a, removed_by_user: a.removed })),
      })),
    );
  }

  /**
   * The plan's dollars as the outline now stands (8.11): each task's own amount at the rates
   * the plan was cut with, a group's roll-up, the total. Null when the plan carries no rates —
   * one cut before the sheets existed — and the column then says so rather than showing zeros.
   */
  get _amounts() {
    if (!this._plan?.rates) return null;
    return planAmounts(
      this._plan.tasks.map((t) => ({
        ...t,
        removed_by_user: t.removed,
        assignments: t.assignments.map((a) => ({ ...a, removed_by_user: a.removed })),
      })),
      this._plan.rates,
    );
  }

  /** Apply an edit to the plan on screen, and queue it. */
  _editTask(id, patch) {
    this._plan = {
      ...this._plan,
      tasks: this._plan.tasks.map((t) => (t.id === id ? { ...t, ...patch, edited: true } : t)),
    };
    const queued = this._queue.tasks.get(id) ?? { id };
    this._queue.tasks.set(id, { ...queued, ...toWire(patch) });
    this._flushSoon();
  }

  _editAssignment(taskId, id, patch) {
    this._plan = {
      ...this._plan,
      tasks: this._plan.tasks.map((t) =>
        t.id !== taskId
          ? t
          : {
              ...t,
              assignments: t.assignments.map((a) =>
                a.id === id ? { ...a, ...patch, edited: true } : a,
              ),
            },
      ),
    };
    const queued = this._queue.assignments.get(id) ?? { id };
    this._queue.assignments.set(id, { ...queued, ...toWire(patch) });
    this._flushSoon();
  }

  _flushSoon() {
    clearTimeout(this._timer);
    this._timer = setTimeout(() => this._flush(), SAVE_DEBOUNCE_MS);
  }

  async _flush() {
    const tasks = [...this._queue.tasks.values()];
    const assignments = [...this._queue.assignments.values()];
    if (!tasks.length && !assignments.length) return;
    this._queue = { tasks: new Map(), assignments: new Map() };
    this._saving = true;
    try {
      this._plan = await api.patch(`/estimate/schedule/plans/${this._plan.id}`, {
        tasks,
        assignments,
      });
      this._error = null;
    } catch (err) {
      // The screen keeps the edit and says the save failed, rather than silently reverting work
      // somebody just did. Re-queued, so the next change carries it.
      for (const t of tasks) this._queue.tasks.set(t.id, t);
      for (const a of assignments) this._queue.assignments.set(a.id, a);
      this._error = `Could not save that edit: ${err.message}`;
    } finally {
      this._saving = false;
    }
  }

  /** Point the plan at a project — the one edit that needs the server before it is believed. */
  async _setTargetProject(number) {
    this._set('project_number', number);
    this._set('confirm', false);
    await this._syncTarget();
  }

  /**
   * Point the stored plan at the chosen project — the one edit the server has to agree to before
   * the screen believes it, because it re-reads the project's task count from Workamajig.
   *
   * Held back while a project with a schedule is unconfirmed. The refusal is correct and the
   * endpoint keeps it, but the estimator is already looking at the warning that explains it, and
   * an error message underneath saying the same thing in worse words is noise. The confirm ticks,
   * and this runs.
   */
  async _syncTarget() {
    const f = this._form;
    if (!this._plan) return;
    if (this._needsConfirm && !f.confirm) return;
    try {
      this._plan = await api.patch(`/estimate/schedule/plans/${this._plan.id}`, {
        target_project_number: f.project_number || null,
        confirm_existing_tasks: Boolean(f.confirm),
      });
      this._error = null;
    } catch (err) {
      this._error = err.message;
    }
  }

  _toggleGroup(order) {
    const next = new Set(this._collapsed);
    if (next.has(order)) next.delete(order);
    else next.add(order);
    this._collapsed = next;
  }

  /** Whether a task is hidden because one of its ancestors is collapsed. */
  _hidden(task) {
    let parent = task.parent_order;
    const byOrder = new Map(this._plan.tasks.map((t) => [t.display_order, t]));
    for (let guard = 0; guard < 20 && parent !== null && parent !== undefined; guard++) {
      if (this._collapsed.has(parent)) return true;
      parent = byOrder.get(parent)?.parent_order ?? null;
    }
    return false;
  }

  // ------------------------------------------------------------------------------------------

  _setupCard() {
    const f = this._form;
    const projects = this._projects?.projects ?? [];
    const project = this._project;
    const active = this._companies.filter((c) => c.is_active_client);
    const rest = this._companies.filter((c) => !c.is_active_client);
    const option = (c) =>
      html`<option value=${c.id} ?selected=${c.id === f.company_id}>${c.name}</option>`;

    return html`
      <div class="card">
        <div class="eyebrow">Create Schedule</div>
        <h2>${this._estimate?.project_name ?? 'Schedule'}</h2>
        <p class="sub">
          A schedule is written into a project that already exists in Workamajig. Pick the client
          and the project, give the delivery date, and review what comes back before anything is
          written.
        </p>

        <div class="setup">
          <div class="field">
            <label for="client">Client</label>
            <select
              id="client"
              @change=${(e) => {
                this._set('company_id', e.target.value || null);
                this._set(
                  'company_name',
                  this._companies.find((c) => c.id === e.target.value)?.name ?? '',
                );
                this._set('project_number', '');
                this._loadProjects();
              }}
            >
              <option value="" ?selected=${!f.company_id}>
                ${f.company_name || 'Choose a client…'}
              </option>
              ${active.map(option)}${rest.map(option)}
            </select>
          </div>

          <div class="field">
            <label for="project">Project</label>
            <select
              id="project"
              @change=${(e) => this._setTargetProject(e.target.value)}
              .value=${f.project_number}
            >
              <option value="" ?selected=${!f.project_number}>Choose a project…</option>
              ${projects.map(
                (p) =>
                  html`<option
                    value=${p.project_number}
                    ?selected=${p.project_number === f.project_number}
                  >
                    ${p.project_number} —
                    ${p.name}${
                      p.task_count ? ` · ${p.task_count} tasks` : ''
                    }${p.is_open ? '' : ' · closed'}
                  </option>`,
              )}
            </select>
            <span class="hint">
              ${
                projects.length
                  ? html`${count(projects.length)} project${projects.length === 1 ? '' : 's'} for
                    this client, as of the last sync.`
                  : 'No projects for this client have been synced yet.'
              }
            </span>
          </div>

          <div class="field">
            <span class="label-as-text">Size</span>
            ${this._sizeSummary()}
          </div>

          <div class="field">
            <label for="budget">Budget</label>
            <input
              id="budget"
              type="number"
              min="0"
              step="1"
              placeholder="No set budget"
              .value=${f.budget_amount === null || f.budget_amount === undefined ? '' : String(f.budget_amount)}
              @input=${(e) => this._set('budget_amount', e.target.value)}
            />
            <span class="hint">
              Dollars. Set, the plan is scaled to come to this at the sheet’s rates.
              ${
                this._estimate?.budget_amount &&
                String(this._form.budget_amount) === String(this._estimate.budget_amount)
                  ? ' From this estimate.'
                  : ''
              }
            </span>
          </div>

          <div class="field">
            <label for="rate_sheet">Rate sheet</label>
            <select id="rate_sheet" @change=${(e) => this._set('rate_sheet_id', e.target.value)}>
              <option value="" ?selected=${!f.rate_sheet_id}>
                ${this._defaultSheet ? `${this._defaultSheet.name} — the default` : 'The default sheet'}
              </option>
              ${this._rateSheets
                .filter((r) => !r.is_default)
                .map(
                  (r) =>
                    html`<option value=${r.id} ?selected=${f.rate_sheet_id === r.id}>
                      ${r.name}
                    </option>`,
                )}
            </select>
            <span class="hint"
              >One hourly rate per service; the budget is turned into hours through it.</span
            >
          </div>

          <div class="field">
            <label for="target">Target hours</label>
            <input
              id="target"
              type="number"
              min="0"
              step="0.25"
              placeholder=${Number(f.budget_amount) > 0 ? 'From the budget' : 'From the analogs'}
              .value=${String(f.target_hours ?? '')}
              @input=${(e) => this._set('target_hours', e.target.value)}
            />
            <span class="hint">
              ${
                Number(f.budget_amount) > 0
                  ? 'Left empty, what the budget buys at the rate sheet. A number here overrides the budget.'
                  : 'Left empty, the analog set’s weighted median.'
              }
            </span>
          </div>

          <div class="field">
            <label for="anchor">Placed by</label>
            <select id="anchor" @change=${(e) => this._set('anchor', e.target.value)}>
              <option value="delivery" ?selected=${f.anchor === 'delivery'}>
                Delivery date — derive the start
              </option>
              <option value="span" ?selected=${f.anchor === 'span'}>
                A fixed window — stretch the work
              </option>
            </select>
          </div>

          <div class="field">
            <label for="start">Start${f.anchor === 'span' ? '' : ' (optional)'}</label>
            <input
              id="start"
              type="date"
              .value=${f.start_date ?? ''}
              @input=${(e) => this._set('start_date', e.target.value)}
            />
            <span class="hint">
              ${
                f.anchor === 'span'
                  ? 'The work is stretched between these two dates.'
                  : 'Checked against the start the plan needs, not built from.'
              }
            </span>
          </div>

          <div class="field">
            <label for="delivery">Delivery</label>
            <input
              id="delivery"
              type="date"
              .value=${f.delivery_date ?? ''}
              @input=${(e) => this._set('delivery_date', e.target.value)}
            />
            <span class="hint">
              The commitment. The schedule is built backwards from it.
              ${
                this._estimate?.delivery_date &&
                this._form.delivery_date === this._estimate.delivery_date
                  ? ' From this estimate.'
                  : ''
              }
            </span>
          </div>
        </div>

        ${
          this._needsConfirm
            ? html`<div class="warn">
                <strong
                  >${project.project_number} already carries ${count(project.task_count)}
                  task${project.task_count === 1 ? '' : 's'}.</strong
                >
                Writing a second schedule onto it cannot be undone through Workamajig’s API — a
                duplicate is cleared up by hand, task by task, in Workamajig itself.
                <label>
                  <input
                    type="checkbox"
                    .checked=${Boolean(f.confirm)}
                    @change=${(e) => {
                      this._set('confirm', e.target.checked);
                      this._syncTarget();
                    }}
                  />
                  I have checked this project, and this schedule belongs on it.
                </label>
              </div>`
            : nothing
        }

        <div class="actions">
          <button class="primary" ?disabled=${!this._canGenerate} @click=${() => this._generate()}>
            ${
              this._generating
                ? 'Generating…'
                : this._plan
                  ? 'Generate again'
                  : 'Generate the schedule'
            }
          </button>
          <button @click=${() => navigate(`/estimate/${this._id}`)}>Back to the estimate</button>
          ${
            this._plan && !this._editable
              ? html`<span class="hint">
                  ${
                    this._plan.status === 'approved'
                      ? 'This plan is approved and frozen. Send it back to draft to change or regenerate it. Nothing has been written to Workamajig.'
                      : this._plan.status === 'abandoned'
                        ? 'This plan was set aside. Generating creates a new plan beside it; the old one stays as the record of what it wrote.'
                        : `This plan is ${this._plan.status.replace('_', ' ')} and is the record of what reached Workamajig. Start over below to generate a new one.`
                  }
                </span>`
              : this._plan
                ? html`<span class="hint">
                    Generating again replaces this draft. Nothing is written to Workamajig either
                    way.
                  </span>`
                : nothing
          }
          ${this._error ? html`<span class="error">${this._error}</span>` : nothing}
        </div>
      </div>
    `;
  }

  /**
   * Where the size came from, in place of the control that used to ask for it.
   *
   * The Size selector moved to the estimate editor (6.8) when the size analog became a *pricing*
   * input: it decides which past projects are comparable, so it has to be chosen before there is
   * a price to look at. Leaving a second copy here would let an estimate and its schedule
   * disagree about how big the job is — the estimate saying Medium to the client and the
   * schedule cutting an Enterprise template — and nothing on either screen would have said so.
   *
   * So this states the answer and points at where to change it. An estimate with no size still
   * gets a schedule: the tier falls back to the recommendation from the analog set, exactly as
   * it did before.
   */
  _sizeSummary() {
    const chosen = this._estimate?.size_tier ?? null;
    // The **size** ladder, from the bootstrap — not the template ladder this screen also holds.
    // A rung's size and its template's total are allowed to differ (Landing Page is 4-16 h and
    // cuts from a 27.5 h template), and this field is labelled Size. The template and its hours
    // are named in the totals card below, where they belong.
    const row = (this.appState.estimate.data?.ladder ?? []).find((t) => t.tier === chosen);
    // The plan on screen was cut from some rung; if the estimate has since said a different
    // one, the plan below is stale. Saying so is the whole reason this reads off the estimate
    // rather than off the plan: two different answers with nothing marking the difference is
    // exactly what moving this control to the estimate was meant to stop.
    const stale = this._plan && chosen && this._plan.tier !== chosen;
    return html`
      <div class="inherited">
        ${
          chosen
            ? html`<strong>${sizeTierLabel(chosen)}</strong>${
                  row?.label ? html` — ${row.label}` : nothing
                }`
            : html`<strong>Not sized</strong>`
        }
        <span class="hint">
          ${
            chosen
              ? html`From this estimate's size analog.
                  <button class="link" @click=${() => navigate(`/estimate/${this._id}`)}>
                    Change it on the estimate</button
                  >.`
              : html`This estimate has no size, so the tier is recommended from the analogs.
                  <button class="link" @click=${() => navigate(`/estimate/${this._id}`)}>
                    Set one on the estimate</button
                  >.`
          }
        </span>
        ${
          stale
            ? html`<span class="hint stale">
                The plan below <strong>was cut from ${sizeTierLabel(this._plan.tier)}</strong>.
                Generate again to rebuild it at ${sizeTierLabel(chosen)}.
              </span>`
            : nothing
        }
        ${this._recommendation()}
      </div>
    `;
  }

  /** What the analogs argued for, named, because the gate asks the screen to name them. */
  _recommendation() {
    const r = this._plan?.recommendation;
    if (!r?.recommended_tier) return nothing;
    const top = (r.analogs ?? []).slice(0, 3);
    return html`<span class="hint">
      ${sizeTierLabel(r.recommended_tier)} recommended from ${count(r.analogs_with_hours)}
      analog${r.analogs_with_hours === 1 ? '' : 's'} at
      ${fmtHours(r.analog_hours)}${
        top.length ? html` — ${top.map((a) => a.number).join(', ')}` : nothing
      }.
    </span>`;
  }

  _totalsCard() {
    const plan = this._plan;
    const totals = this._totals;
    const off = Math.abs(totals.total_hours - plan.target_hours) >= 0.005;
    const s = plan.schedule ?? {};
    return html`
      <div class="card">
        <div class="totals">
          <div class="stat">
            <span class="k">Total</span>
            <span class="v ${off ? 'off' : ''}"
              >${fmtHours(totals.total_hours, { exact: true })}</span
            >
            <span class="n">
              ${
                off
                  ? html`${totals.total_hours > plan.target_hours ? 'over' : 'under'} the
                    ${fmtHours(plan.target_hours, { exact: true })} target`
                  : 'reconciles to the target'
              }
            </span>
          </div>
          ${
            plan.plan_amount !== null && plan.plan_amount !== undefined
              ? html`<div class="stat">
                  <span class="k">Amount</span>
                  <span
                    class="v ${plan.budget_amount !== null && Math.abs(plan.budget_residual ?? 0) >= 0.005 ? 'off' : ''}"
                    >${money(plan.plan_amount)}</span
                  >
                  <span class="n">
                    ${
                      plan.budget_amount !== null && plan.budget_amount !== undefined
                        ? Math.abs(plan.budget_residual ?? 0) < 0.005
                          ? html`meets the ${money(plan.budget_amount)} budget`
                          : html`${plan.budget_residual > 0 ? 'under' : 'over'} the
                            ${money(plan.budget_amount)} budget by
                            ${money(Math.abs(plan.budget_residual))}`
                        : 'at the rate sheet'
                    }${plan.rate_sheet_name ? html` · ${plan.rate_sheet_name}` : nothing}
                  </span>
                </div>`
              : nothing
          }
          <div class="stat">
            <span class="k">Tasks</span>
            <span class="v">${count(totals.active_tasks)}</span>
            <span class="n">
              ${count(totals.active_assignments)}
              line${totals.active_assignments === 1 ? '' : 's'}${
                totals.removed_hours ? html`, ${fmtHours(totals.removed_hours)} removed` : nothing
              }
            </span>
          </div>
          <div class="stat">
            <span class="k">Template</span>
            <span class="v">${sizeTierLabel(plan.tier)}</span>
            <span class="n"
              >${
                plan.template_version_number
                  ? `version ${plan.template_version_number}`
                  : plan.template_project_number
              }
              ·
              ${fmtHours(plan.template_hours)}${
                // The rung has been re-published since this plan was cut (Estimate Tool 8.10).
                // Said here, because two answers with nothing marking the difference is exactly
                // what versioning the templates was meant to stop.
                plan.template_superseded
                  ? html` ·
                      <span
                        class="mark"
                        title="This rung's template has been re-published since this plan was generated; generate again to cut from the current version"
                        >template superseded</span
                      >`
                  : nothing
              }</span
            >
          </div>
          <div class="stat">
            <span class="k">Runs</span>
            <span class="v">${mediumDate(plan.start_date)}</span>
            <span class="n">
              to
              ${mediumDate(plan.delivery_date)}${
                s.plan_weekdays ? ` · ${s.plan_weekdays} working days` : ''
              }
            </span>
          </div>
          ${
            plan.rehomed_hours
              ? html`<div class="stat">
                  <span class="k">Re-homed</span>
                  <span class="v">${fmtHours(plan.rehomed_hours)}</span>
                  <span class="n">off services Workamajig has deactivated</span>
                </div>`
              : nothing
          }
        </div>
        ${
          s.starts_before_requested
            ? html`<p class="warn">
                This plan has to begin on <strong>${mediumDate(plan.start_date)}</strong>, which is
                before the start that was asked for. The delivery date is the commitment, so the
                work moves rather than the date.
              </p>`
            : nothing
        }
        <div class="services">
          ${totals.by_service.map(
            (row) =>
              html`<span class="chip" title=${row.service_name ?? row.service_code}>
                ${row.service_code} ${fmtHours(row.hours, { exact: true, unit: false })}
              </span>`,
          )}
        </div>
      </div>
    `;
  }

  /** Whether this plan may still be edited. The server decides; the screen asks it. */
  get _editable() {
    return this._plan?.can_edit !== false;
  }

  /**
   * The approval gate (Estimate Tool 8.7).
   *
   * It sits between the totals and the outline on purpose: a person approves after reading the
   * numbers and before going back into the detail, and a gate below seventy-two task rows is a
   * gate nobody reaches.
   *
   * What it says before the approval is what the write cannot take back (8.8) — a repeated task
   * name creates a second task, `DELETE /tasks` deletes nothing, and the cleanup is by hand in
   * Workamajig. What it says after is who approved what, and how to get back.
   */
  _approvalCard() {
    const plan = this._plan;
    const totals = this._totals;
    const approved = plan.status === 'approved';
    if (plan.status === 'writing') return this._writingCard();
    if (plan.status === 'written' || plan.status === 'partially_written')
      return this._writtenCard();
    if (plan.status === 'abandoned') return this._abandonedCard();
    if (plan.status !== 'draft' && !approved) {
      return html`<div class="card">
        <div class="approval done">
          <strong>This plan is ${plan.status}.</strong>
          It can no longer be edited or sent back to draft. Changing this schedule means generating
          a new plan.
        </div>
      </div>`;
    }

    if (approved) {
      const blocker = plan.write_blocker?.message ?? null;
      return html`<div class="card">
        <div class="approval approved">
          <strong>Approved${plan.approved_by ? ` by ${plan.approved_by}` : ''}</strong>
          ${plan.approved_at ? html` on ${mediumDate(plan.approved_at.slice(0, 10))}` : nothing}, at
          ${fmtHours(plan.approved_total_hours ?? totals.total_hours, { exact: true })} across
          ${count(plan.approved_task_count ?? totals.active_tasks)} tasks, for
          <strong>${plan.target_project_number}</strong>.
          ${plan.approval_note ? html`<em>“${plan.approval_note}”</em>` : nothing}
          <p class="hint">
            The plan is locked while it is approved — that is what approving it means. Nothing has
            been written to Workamajig yet. Writing it creates ${count(totals.active_tasks)}
            ${totals.active_tasks === 1 ? 'task' : 'tasks'} in ${plan.target_project_number}, one at
            a time, about three minutes for a Medium — and it cannot be undone here: a task written
            twice is deleted by hand, in Workamajig.
          </p>
          <div class="approval-actions">
            <button
              class="primary"
              ?disabled=${Boolean(blocker) || this._writing || this._approving}
              @click=${() => this._write()}
            >
              ${this._writing ? 'Starting…' : 'Write to Workamajig'}
            </button>
            <button ?disabled=${this._approving || this._writing} @click=${() => this._unapprove()}>
              ${this._approving ? 'Working…' : 'Send back to draft'}
            </button>
            ${blocker ? html`<span class="hint">${blocker}</span>` : nothing}
            ${this._writeError ? html`<span class="error">${this._writeError}</span>` : nothing}
          </div>
        </div>
      </div>`;
    }

    const blocker = this._approvalBlocker();
    return html`<div class="card">
      <div class="approval">
        <strong>Nothing has been written to Workamajig.</strong>
        This plan is a draft. Approving it freezes it and is what lets it be written — and the write
        cannot be undone: a task written twice is deleted by hand, in Workamajig, one at a time.
        <p class="hint">
          ${fmtHours(totals.total_hours, { exact: true })} across ${count(totals.active_tasks)}
          ${totals.active_tasks === 1 ? 'task' : 'tasks'} and ${count(totals.active_assignments)}
          ${totals.active_assignments === 1 ? 'line' : 'lines'}${
            totals.removed_hours ? html`, with ${fmtHours(totals.removed_hours)} removed` : nothing
          }${
            this._plan.totals?.edited_assignments
              ? html`, ${count(this._plan.totals.edited_assignments)} edited by hand`
              : nothing
          }.
        </p>
        <div class="approval-actions">
          <button
            class="primary"
            ?disabled=${Boolean(blocker) || this._approving}
            @click=${() => this._approve()}
          >
            ${this._approving ? 'Approving…' : 'Approve this schedule'}
          </button>
          ${blocker ? html`<span class="hint">${blocker}</span>` : nothing}
          ${this._approveError ? html`<span class="error">${this._approveError}</span>` : nothing}
        </div>
      </div>
    </div>`;
  }

  /**
   * Why the Approve button is disabled, in the screen's own words — or null.
   *
   * The same three conditions the server refuses on, tested here so the button is dead rather
   * than lively-and-then-rejected. The server is still the one that decides; this is not a
   * second rule, it is the same rule said early.
   */
  _approvalBlocker() {
    const plan = this._plan;
    const totals = this._totals;
    if (!plan.target_project_number)
      return 'Choose the Workamajig project this schedule goes into first — a schedule has nowhere to land without one.';
    if (!totals.active_tasks) return 'Every task has been removed, so there is nothing to write.';
    if (Math.abs(totals.total_hours - plan.target_hours) >= 0.005)
      return `The plan totals ${fmtHours(totals.total_hours, { exact: true })} against a target of ${fmtHours(plan.target_hours, { exact: true })}. Make the two agree before approving.`;
    return null;
  }

  async _approve() {
    this._approving = true;
    this._approveError = null;
    try {
      this._plan = await api.post(`/estimate/schedule/plans/${this._plan.id}/approve`, {});
    } catch (err) {
      this._approveError = err.message;
    } finally {
      this._approving = false;
    }
  }

  async _unapprove() {
    this._approving = true;
    this._approveError = null;
    try {
      this._plan = await api.post(`/estimate/schedule/plans/${this._plan.id}/unapprove`, {});
    } catch (err) {
      this._approveError = err.message;
    } finally {
      this._approving = false;
    }
  }

  // ------------------------------------------------------------------------------------------
  // The write (Estimate Tool 8.8; Phase E11). Started here, run on the server as a job, watched
  // from here: the plan's own lines carry their status, so the screen polls the plan.

  /** Start the write, or resume one that stopped part-way. */
  async _write() {
    this._writing = true;
    this._writeError = null;
    try {
      const { plan } = await api.post(`/estimate/schedule/plans/${this._plan.id}/write`, {});
      this._plan = plan;
      this._watchWrite();
    } catch (err) {
      this._writeError = err.message;
    } finally {
      this._writing = false;
    }
  }

  /** Re-read the plan every couple of seconds until the job has finished. */
  _watchWrite() {
    clearTimeout(this._pollTimer);
    this._pollTimer = setTimeout(async () => {
      if (!this._plan) return;
      try {
        const plan = await api.get(`/estimate/schedule/plans/${this._plan.id}`);
        this._plan = plan;
        if (plan.status === 'writing') this._watchWrite();
      } catch (err) {
        this._writeError = err.message;
      }
    }, WRITE_POLL_MS);
  }

  /** What is going on, while it is going on: which task the job is on, of how many. */
  _writingCard() {
    const plan = this._plan;
    const w = plan.write ?? {};
    const done = (w.tasks_written ?? 0) + (w.tasks_skipped ?? 0) + (w.tasks_failed ?? 0);
    const total = w.tasks_total ?? this._totals.active_tasks;
    const pct = total ? Math.min(100, Math.round((done / total) * 100)) : 0;
    return html`<div class="card">
      <div class="approval">
        <strong>Writing into ${plan.target_project_number}…</strong>
        ${count(done)} of ${count(total)} tasks so
        far${
          w.tasks_failed
            ? html`, <span class="error">${count(w.tasks_failed)} failed</span>`
            : nothing
        }.
        One task lands every couple of seconds; the outline marks each as it does.
        <div
          class="progress"
          role="progressbar"
          aria-valuenow=${pct}
          aria-valuemin="0"
          aria-valuemax="100"
        >
          <div style="width: ${pct}%"></div>
        </div>
        <p class="hint">
          Leaving this page does not stop the write. When every line has landed the project is read
          back from Workamajig and compared, task for task, with what was approved.
        </p>
      </div>
    </div>`;
  }

  /** What happened: the record, the read-back's findings, and the way to resume a partial write. */
  _writtenCard() {
    const plan = this._plan;
    const w = plan.write ?? null;
    const rb = w?.readback ?? null;
    const partial = plan.status === 'partially_written';
    const remaining = plan.tasks.filter(
      (t) => !t.removed && t.write_status !== 'written' && !this._underRemoved(t),
    );
    const owed = plan.tasks.filter(
      (t) =>
        !t.removed &&
        t.write_status === 'written' &&
        (t.predecessor_orders ?? []).length &&
        !t.predecessors_written_at &&
        !this._underRemoved(t),
    );
    const failed = plan.tasks.filter((t) => t.write_status === 'failed');
    const landed = plan.tasks.filter((t) => t.write_status === 'written');
    // Quarter hours to the quarter: the hours formatter rounds to a tenth, and a finding about
    // 9.75 against 10 has to say 9.75.
    const qh = (v) =>
      `${Number(v)
        .toFixed(2)
        .replace(/\.?0+$/, '')} h`;
    const findings = rb
      ? [
          ...rb.tasks_missing.map((m) => `“${m.name}” was not found in the project on read-back`),
          ...rb.tasks_unverifiable.map(
            (m) =>
              `“${m.name}” could not be checked: the two Workamajig surfaces disagree at its position`,
          ),
          ...rb.name_mismatches.map((m) => `“${m.sent}” reads back as “${m.read}”`),
          ...rb.parent_mismatches.map(
            (m) => `“${m.name}” is nested under a different task than the plan says`,
          ),
          ...rb.assignments_missing.map(
            (m) => `“${m.name}” has no ${m.service_code} line in Workamajig (${qh(m.hours)} sent)`,
          ),
          ...rb.hours_mismatches.map(
            (m) => `“${m.name}” ${m.service_code}: sent ${qh(m.sent)}, reads ${qh(m.read)}`,
          ),
          ...(rb.predecessor_mismatches ?? []).map(
            (m) =>
              `“${m.name}” depends on ${m.read.length ? `task${m.read.length === 1 ? '' : 's'} ${m.read.join(', ')}` : 'nothing'} in Workamajig, not ${m.sent.length ? `task${m.sent.length === 1 ? '' : 's'} ${m.sent.join(', ')}` : 'nothing'} as sent`,
          ),
          ...(rb.total_hours_match === false
            ? [
                `the project reads ${qh(rb.hours_read)} against ${qh(rb.approved_total_hours)} approved`,
              ]
            : []),
        ]
      : [];
    return html`<div class="card">
      <div class="approval ${partial ? '' : 'done'}">
        <strong>
          ${
            partial
              ? `Partly written into ${plan.target_project_number}.`
              : `Written into ${plan.target_project_number}${plan.written_by ? ` by ${plan.written_by}` : ''}${
                  plan.written_at ? ` on ${mediumDate(plan.written_at.slice(0, 10))}` : ''
                }.`
          }
        </strong>
        ${
          partial
            ? html`${
                remaining.length
                  ? html`${count(remaining.length)}
                    ${remaining.length === 1 ? 'task has' : 'tasks have'} not
                    landed${failed.length ? html`, ${count(failed.length)} of them refused` : nothing}.`
                  : nothing
              }${
                owed.length
                  ? html` ${count(owed.length)} ${owed.length === 1 ? 'task is' : 'tasks are'} still
                    waiting for ${owed.length === 1 ? 'its' : 'their'} dependencies to be drawn.`
                  : nothing
              }
              The lines that did land are marked below and will not be sent again; resuming sends
              only the rest.${w?.error ? html` <span class="error">${w.error}</span>` : nothing}`
            : html`This plan is the record of what is in Workamajig, so it can no longer be edited
              or sent back to draft. Changing the schedule means generating a new plan.`
        }
        ${
          rb
            ? html`<p class="hint">
                  Read back${w.readback_at ? ` on ${mediumDate(w.readback_at.slice(0, 10))}` : ''}:
                  ${count(rb.tasks_matched)} of ${count(rb.tasks_checked)} tasks and
                  ${count(rb.assignments_matched)} of ${count(rb.assignments_checked)} lines match
                  what was sent, ${fmtHours(rb.hours_read, { exact: true })} in the
                  project${
                    w.predecessors_written
                      ? html`, ${count(w.predecessors_written)}
                        ${w.predecessors_written === 1 ? 'dependency' : 'dependencies'} drawn`
                      : nothing
                  }${
                    (rb.date_drift ?? []).length
                      ? html`; the scheduler moved ${count(rb.date_drift.length)}
                        ${rb.date_drift.length === 1 ? 'task' : 'tasks'} off the planned start`
                      : nothing
                  }.
                  ${
                    w.verified
                      ? html`<strong>Every line reconciles.</strong>`
                      : html`<strong
                            >${count(findings.length)}
                            ${findings.length === 1 ? 'finding' : 'findings'}</strong
                          >
                          for the gate — recorded, not retried: a task that landed is a task that
                          landed.`
                  }
                </p>
                ${
                  findings.length
                    ? html`<ul class="findings">
                        ${findings.map((f) => html`<li>${f}</li>`)}
                      </ul>`
                    : nothing
                }`
            : w?.error
              ? html`<p class="hint">Not read back: ${w.error}</p>`
              : nothing
        }
        <div class="approval-actions">
          ${
            partial
              ? html`<button
                  class="primary"
                  ?disabled=${this._writing || Boolean(plan.write_blocker)}
                  @click=${() => this._write()}
                >
                  ${this._writing ? 'Starting…' : 'Resume the write'}
                </button>`
              : nothing
          }
          <button ?disabled=${this._writing || this._approving} @click=${() => this._abandon()}>
            ${this._approving ? 'Working…' : 'Start over'}
          </button>
          <span class="hint">
            Start over sets this plan aside and lets a new one be generated. It deletes nothing:
            remove the ${count(landed.length)} ${landed.length === 1 ? 'task' : 'tasks'} it wrote
            from ${plan.target_project_number} in Workamajig by hand first, or the new plan will
            land beside them.
          </span>
          ${partial && plan.write_blocker ? html`<span class="hint">${plan.write_blocker.message}</span>` : nothing}
          ${this._writeError ? html`<span class="error">${this._writeError}</span>` : nothing}
          ${this._approveError ? html`<span class="error">${this._approveError}</span>` : nothing}
        </div>
      </div>
    </div>`;
  }

  /** Set a written plan aside so the estimate can be scheduled again (8.7). */
  async _abandon() {
    this._approving = true;
    this._approveError = null;
    try {
      this._plan = await api.post(`/estimate/schedule/plans/${this._plan.id}/abandon`, {});
    } catch (err) {
      this._approveError = err.message;
    } finally {
      this._approving = false;
    }
  }

  /** A plan set aside: the record of what it wrote, and the way forward. */
  _abandonedCard() {
    const plan = this._plan;
    const landed = plan.tasks.filter((t) => t.write_status === 'written');
    return html`<div class="card">
      <div class="approval done">
        <strong
          >Set
          aside${plan.abandoned_by ? ` by ${plan.abandoned_by}` : ''}${
            plan.abandoned_at ? ` on ${mediumDate(plan.abandoned_at.slice(0, 10))}` : ''
          }.</strong
        >
        This plan wrote ${count(landed.length)} ${landed.length === 1 ? 'task' : 'tasks'} into
        ${plan.target_project_number ?? 'Workamajig'} and stays as the record of that. It cannot be
        edited or written again. To start over, remove those tasks in Workamajig — this tool cannot
        delete them — then generate a new plan above; a project that still carries tasks asks for a
        confirm first. ${plan.abandon_reason ? html`<em>“${plan.abandon_reason}”</em>` : nothing}
      </div>
    </div>`;
  }

  /**
   * The tasks this one follows (8.8, 8.10), by name, under the task: the dependencies the write
   * draws once every task has its id. A predecessor that has been removed is struck through, so
   * the reader can see which arrow will not be drawn.
   */
  _afterHint(task) {
    const orders = task.predecessor_orders ?? [];
    if (!orders.length) return nothing;
    const byOrder = new Map(this._plan.tasks.map((t) => [t.display_order, t]));
    return html`<div class="after">
      After
      ${orders.map((o, i) => {
        const p = byOrder.get(o);
        const gone = !p || p.removed || this._underRemoved(p);
        return html`${i ? ', ' : ' '}<span
            class=${gone ? 'gone' : ''}
            title=${gone ? 'Removed from the plan; this dependency is not written' : `Task ${o}`}
            >${p?.name ?? `task ${o}`}</span
          >`;
      })}
    </div>`;
  }

  /** Whether a task sits under a removed group, and so was never going to be written. */
  _underRemoved(task) {
    const byOrder = new Map(this._plan.tasks.map((t) => [t.display_order, t]));
    let parent = task.parent_order;
    for (let guard = 0; guard < 20 && parent !== null && parent !== undefined; guard++) {
      const p = byOrder.get(parent);
      if (!p) return false;
      if (p.removed) return true;
      parent = p.parent_order;
    }
    return false;
  }

  _outlineCard() {
    const plan = this._plan;
    const totals = this._totals;
    const amounts = this._amounts;
    const visible = plan.tasks.filter((t) => (this._showRemoved || !t.removed) && !this._hidden(t));
    return html`
      <div class="card">
        <div class="toolbar">
          <h3>The plan</h3>
          ${
            // Said on the outline as well as on the approval card. The card explains the state;
            // this explains why the box under the reader's cursor will not take a keystroke.
            this._editable
              ? nothing
              : html`<span class="locked">
                  ${
                    this._plan.status === 'approved'
                      ? 'Locked — approved, and not yet written'
                      : this._plan.status === 'writing'
                        ? 'Locked — being written'
                        : `Locked — ${this._plan.status.replace('_', ' ')}`
                  }
                </span>`
          }
          <label>
            <input
              type="checkbox"
              .checked=${this._showRemoved}
              @change=${(e) => (this._showRemoved = e.target.checked)}
            />
            Show removed lines
          </label>
          ${this._saving ? html`<span class="saving">Saving…</span>` : nothing}
        </div>
        <div class="scroll">
          <table>
            <thead>
              <tr>
                <th>Task</th>
                <th>Dates</th>
                <th>Services and hours</th>
                <th class="num">Hours</th>
                <th
                  class="num"
                  title=${plan.rate_sheet_name ? `At ${plan.rate_sheet_name}` : 'This plan carries no rate sheet'}
                >
                  Amount
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              ${visible.map((t) => this._taskRow(t, totals, amounts))}
            </tbody>
            <tfoot>
              <tr class="total">
                <td colspan="3">
                  Project
                  total${plan.rate_sheet_name ? html` <span class="hint">at ${plan.rate_sheet_name}</span>` : nothing}
                </td>
                <td class="num">${fmtHours(totals.total_hours, { exact: true, unit: false })}</td>
                <td class="num">
                  ${
                    amounts
                      ? html`${money(amounts.total)}${
                          amounts.unrated_hours
                            ? html` <span
                                class="mark"
                                title="Hours on a service the sheet does not price"
                                >+ ${fmtHours(amounts.unrated_hours, { exact: true })}
                                unpriced</span
                              >`
                            : nothing
                        }`
                      : html`<span
                          class="hint"
                          title="Generate again to price this plan at a rate sheet"
                          >—</span
                        >`
                  }
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    `;
  }

  _taskRow(task, totals, amounts = null) {
    const depth = this._depth(task);
    const subtotal = totals.rollup_hours.get(task.display_order) ?? 0;
    // A group shows the sum of what is beneath it; a task shows its own lines at the rates.
    const amount = amounts
      ? task.is_group
        ? amounts.rollup.get(task.display_order)
        : amounts.own.get(task.display_order)
      : null;
    return html`
      <tr class="${task.is_group ? 'group' : ''} ${task.removed ? 'removed' : ''}">
        <td class="indent-${Math.min(depth, 2)}">
          ${
            task.is_group
              ? html`<button
                  class="icon"
                  aria-expanded=${this._collapsed.has(task.display_order) ? 'false' : 'true'}
                  title="Collapse or expand this group"
                  @click=${() => this._toggleGroup(task.display_order)}
                >
                  ${this._collapsed.has(task.display_order) ? '+' : '−'}
                </button>`
              : nothing
          }
          <input
            class="name"
            .value=${task.name}
            aria-label="Task name"
            ?readonly=${!this._editable}
            @change=${(e) => this._editTask(task.id, { name: e.target.value })}
          />
          ${task.renamed ? html`<span class="mark edited">Renamed</span>` : nothing}
          ${
            task.write_status === 'written'
              ? html`<span
                  class="mark written"
                  title=${
                    task.source_task_id
                      ? `Task ${task.source_task_id} in Workamajig`
                      : 'Written to Workamajig'
                  }
                  >Written</span
                >`
              : task.write_status === 'failed'
                ? html`<span
                    class="mark failed"
                    title=${task.write_error ?? 'This line did not land'}
                    >Not written</span
                  >`
                : nothing
          }
          ${
            task.predecessors_error && !task.predecessors_written_at
              ? html`<span class="mark failed" title=${task.predecessors_error}
                  >Dependencies not set</span
                >`
              : nothing
          }
          ${this._afterHint(task)}
          ${
            task.dead_task_flag
              ? html`<span
                  class="mark"
                  title="Used by ${task.dead_task_refs} of the reference schedules — kept, not removed"
                  >Rarely used</span
                >`
              : nothing
          }
        </td>
        <td>${mediumDate(task.plan_start)} – ${mediumDate(task.plan_complete)}</td>
        <td>
          <div class="lines">
            ${task.assignments
              .filter((a) => this._showRemoved || !a.removed)
              .map((a) => this._assignmentLine(task, a))}
            ${
              task.is_group && !task.assignments.length
                ? html`<span class="hint">Group — the sum of its tasks.</span>`
                : nothing
            }
          </div>
        </td>
        <td class="num">${fmtHours(subtotal, { exact: true, unit: false })}</td>
        <td
          class="num amount"
          title=${task.is_group ? 'The sum of the tasks in this group' : 'This task’s lines at the plan’s rates'}
        >
          ${amount === null || amount === undefined ? '—' : money(amount)}
        </td>
        <td>
          <button
            class="icon"
            ?disabled=${!this._editable}
            title=${task.removed ? 'Put this back in the plan' : 'Remove this from the plan'}
            @click=${() => this._editTask(task.id, { removed: !task.removed })}
          >
            ${task.removed ? 'Restore' : 'Remove'}
          </button>
        </td>
      </tr>
    `;
  }

  /**
   * One assignment: a service, its hours, and a way to drop it. Three controls, and none of them
   * names a person — the plan has no field for one and the schema has no property for one (8.3).
   */
  _assignmentLine(task, a) {
    const moved = a.generated_hours !== null && Math.abs(a.hours - a.generated_hours) >= 0.005;
    const reserviced = a.generated_service_code && a.generated_service_code !== a.service_code;
    return html`
      <div class="line ${a.removed ? 'removed' : ''}">
        <select
          aria-label="Service for ${task.name}"
          ?disabled=${a.removed || !this._editable}
          @change=${(e) => this._editAssignment(task.id, a.id, { service_code: e.target.value })}
        >
          ${this._serviceOptions(a.service_code).map(
            (s) =>
              html`<option value=${s.service_code} ?selected=${s.service_code === a.service_code}>
                ${s.service_code}${s.service_name ? ` — ${s.service_name}` : ''}
              </option>`,
          )}
        </select>
        <input
          type="number"
          min="0"
          step="0.25"
          aria-label="Hours for ${task.name}"
          ?disabled=${a.removed || !this._editable}
          .value=${String(a.hours)}
          @change=${(e) => this._editAssignment(task.id, a.id, { hours: Number(e.target.value) })}
        />
        ${
          a.calibrated && !a.edited
            ? html`<span
                class="mark"
                title="Calibrated against ${a.calibration_sample} reference projects"
                >Calibrated</span
              >`
            : nothing
        }
        ${a.edited ? html`<span class="mark edited">Edited</span>` : nothing}
        ${
          moved
            ? html`<span class="was"
                >was ${fmtHours(a.generated_hours, { exact: true, unit: false })}</span
              >`
            : nothing
        }
        ${reserviced ? html`<span class="was">was ${a.generated_service_code}</span>` : nothing}
        ${
          a.retired_from
            ? html`<span class="was" title="${a.retired_from} has been deactivated in Workamajig"
                >re-homed from ${a.retired_from}</span
              >`
            : nothing
        }
        <button
          class="icon"
          ?disabled=${!this._editable}
          title=${a.removed ? 'Put this line back' : 'Remove this line'}
          @click=${() => this._editAssignment(task.id, a.id, { removed: !a.removed })}
        >
          ${a.removed ? '+' : '×'}
        </button>
      </div>
    `;
  }

  /**
   * The live services, plus whatever this line already names. A plan cut before a service was
   * deactivated still names it, and a picker that dropped the current value would silently change
   * the line the moment anybody touched anything else on it.
   */
  _serviceOptions(current) {
    const list = this._services;
    if (!current || list.some((s) => s.service_code === current)) return list;
    return [{ service_code: current, service_name: null }, ...list];
  }

  _depth(task) {
    const byOrder = new Map(this._plan.tasks.map((t) => [t.display_order, t]));
    let depth = 0;
    let parent = task.parent_order;
    for (let guard = 0; guard < 20 && parent !== null && parent !== undefined; guard++) {
      depth += 1;
      parent = byOrder.get(parent)?.parent_order ?? null;
    }
    return depth;
  }

  render() {
    if (this._loadError)
      return html`<div class="card"><p class="error">${this._loadError}</p></div>`;
    if (!this._form) return html`<div class="card"><p class="state">Loading…</p></div>`;
    return html`
      ${this._setupCard()}
      ${
        this._plan
          ? html`${this._totalsCard()}${this._approvalCard()}${this._outlineCard()}`
          : html`<div class="card">
              <p class="state">
                Pick a project and a delivery date, and the schedule will appear here to review.
                Nothing is written to Workamajig until you say so.
              </p>
            </div>`
      }
    `;
  }
}

/** The screen's spelling of an edit to the API's. */
const toWire = (patch) => {
  const wire = {};
  if (patch.name !== undefined) wire.name = patch.name;
  if (patch.hours !== undefined) wire.hours = patch.hours;
  if (patch.service_code !== undefined) wire.service_code = patch.service_code;
  if (patch.removed !== undefined) wire.removed = patch.removed;
  return wire;
};

customElements.define('estimate-schedule', EstimateSchedule);
